import React, { useState } from 'react';
import { ShieldCheck, Clock, Tag, Star, Laptop } from 'lucide-react';
import { BUSINESS_PHONE, GOOGLE_REVIEWS_URL } from '../constants.tsx';
import { Language, TRANSLATIONS } from '../translations.ts';
import NavigationLink from './NavigationLink.tsx';

interface HeroProps {
  onOpenBooking: () => void;
  onNavigatePricing: () => void;
  lang: Language;
}

const Hero: React.FC<HeroProps> = ({ onOpenBooking, onNavigatePricing, lang }) => {
  const [videoError, setVideoError] = useState(false);
  const t = TRANSLATIONS[lang];
  const videoPlaceholderUrl = "https://assets.mixkit.co/videos/preview/mixkit-circuit-board-details-with-light-passing-by-4482-large.mp4";
  
  // Base image URL
  const heroImageUrl = "https://i.imgur.com/1RjTqsH.jpeg";

  return (
    <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-[#1A365D]">
      <div className="absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
        <img 
            src={heroImageUrl}
            alt="Hero Background" 
            className="w-full h-full object-cover opacity-40 scale-105"
            loading="eager"
            fetchPriority="high" 
        />
        
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A365D] via-[#1A365D]/85 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1A365D]/10 to-[#1A365D]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12 md:py-20 w-full">
        <div className="max-w-3xl animate-in fade-in slide-in-from-left-8 duration-700">
          <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-3 py-1.5 rounded-full text-[10px] md:text-sm font-black uppercase tracking-widest border border-green-500/30 mb-6 backdrop-blur-sm">
            <ShieldCheck size={14} className="md:w-4 md:h-4" />
            {t.hero_badge}
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1] md:leading-[1.1] tracking-tighter mb-6 md:mb-8">
            {t.hero_title} <span className="text-green-400">{t.hero_span}</span>
          </h1>
          
          <p className="text-lg md:text-2xl text-slate-300 mb-8 md:mb-10 leading-relaxed font-light">
            {t.hero_desc}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-10 md:mb-12">
            <button onClick={onOpenBooking} className="bg-[#2F855A] text-white px-8 py-5 rounded-2xl font-black text-lg md:text-xl shadow-2xl hover:bg-[#276749] transition-all transform hover:-translate-y-1 active:scale-95">
              {t.hero_btn_book}
            </button>
            <NavigationLink 
              href="/pricing"
              onClick={onNavigatePricing}
              className="bg-white/10 backdrop-blur-md text-white border-2 border-white/20 px-8 py-5 rounded-2xl font-black text-lg md:text-xl hover:bg-white/20 transition-all text-center inline-flex items-center justify-center cursor-pointer"
            >
              {t.hero_btn_pricing}
            </NavigationLink>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/90 group hover:text-green-400 transition-colors">
              <Star className="text-yellow-400 fill-yellow-400" size={20} />
              <span className="font-black text-xs md:text-base underline decoration-white/20 underline-offset-4 uppercase tracking-wider">⭐ 4.9/5 Google Rating</span>
            </a>
            <div className="flex items-center gap-3 text-white/90"><Clock className="text-yellow-400" size={20} /><span className="font-bold text-xs md:text-base uppercase tracking-wider text-white">Same-Day Service</span></div>
            <div className="flex items-center gap-3 text-white/90"><Tag className="text-yellow-400" size={20} /><span className="font-bold text-xs md:text-base uppercase tracking-wider text-white">No Fix, No Fee</span></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;